import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence, useMotionValue } from 'framer-motion';
import { Zap, Globe, School, X, ArrowLeftRight, Ghost } from 'lucide-react';
import { db, auth } from '../firebase';
import { useIdentity } from '../context/IdentityContext';
import { 
  collection, 
  query, 
  where, 
  onSnapshot, 
  addDoc, 
  deleteDoc, 
  getDocs,
  serverTimestamp,
  getDoc,
  doc,
  updateDoc
} from 'firebase/firestore';

const randomNames = [
  'Mystic', 'Shadow', 'Echo', 'Blaze', 'Luna', 'Nova', 'Sky', 'Zephyr', 'Raven',
  'Vortex', 'Nebula', 'Drift', 'Vibe', 'Frost', 'Phantom', 'Wraith', 'Dusk',
  'Haze', 'Storm', 'Rogue', 'Pulse', 'Ember', 'Flicker', 'Shade', 'Bolt', 'Glimmer',
  'Surge', 'Flare', 'Cosmo', 'Daze', 'Sable', 'Nyx', 'Rift', 'Glow', 'Specter',
  'Chill', 'Havoc', 'Mist', 'Stryke', 'Zest', 'Dawn', 'Volt', 'Crimson', 'Slate',
  'Kaze', 'Fury', 'Vapor', 'Glint', 'Rebel', 'Ash', 'Tide', 'Zenith', 'Dagger',
  'Star', 'Nerve', 'Blitz', 'Shadez', 'Flux', 'Neon', 'Chaos', 'Verve', 'Rush',
  'Grit', 'Faze', 'Drip', 'Hex', 'Jolt', 'Wisp', 'Scorch', 'Vex', 'Hype', 'Frosty',
  'Slash', 'Glaze', 'Sparkz', 'Eclipse', 'Raze', 'Sway', 'Phantomz', 'Buzz', 'Quartz',
  'Vivid', 'RUSH', 'Craze', 'Driftz', 'Ignite', 'Zest', 'Frenzy', 'Spectral', 'BlazeX',
  'Vaporz', 'Sting', 'Aurora', 'Kryo'
];

const statusMessages = [
  'Scanning the frequencies...',
  'Finding your vibe match...',
  'Searching anonymous souls...',
  'Almost there...',
];

export const FindingMatchPage = () => {
  const { identityMode } = useIdentity();
  const navigate = useNavigate();
  const location = useLocation();
  const hasNavigatedRef = useRef(false);
  const queueDocIdRef = useRef(null);
  const minimumTimeRef = useRef(Date.now() + 2500);
  
  const [mode, setMode] = useState('global');
  const [avatarIndex, setAvatarIndex] = useState(0);
  const [statusIndex, setStatusIndex] = useState(0);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [showCampusTimeout, setShowCampusTimeout] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);

  useEffect(() => {
    const avatarInterval = setInterval(() => {
        setAvatarIndex((prev) => (prev + 2) % randomNames.length);
    }, 1500);
    return () => clearInterval(avatarInterval);
  }, []);

  useEffect(() => {
    if (!location.state || !location.state.selectedMode) {
      navigate('/', { replace: true });
    }
  }, [location, navigate]);

  useEffect(() => {
    const statusInterval = setInterval(() => {
        setStatusIndex((prev) => (prev + 1) % statusMessages.length);
    }, 3000);
    return () => clearInterval(statusInterval);
  }, []);

  useEffect(() => {
    const timerInterval = setInterval(() => {
      setTimerSeconds((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(timerInterval);
  }, []);

  useEffect(() => {
    if (mode === 'campus' && timerSeconds >= 5) {
        setShowCampusTimeout(true);
    } else {
        setShowCampusTimeout(false);
    }
  }, [mode, timerSeconds]);

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) {
      navigate('/login');
      return;
    }

    const q1 = query(
      collection(db, 'anonymousChats'),
      where('user1', '==', user.uid),
      where('saved', '==', false),
      where('ended', '==', false)
    );
    const q2 = query(
      collection(db, 'anonymousChats'),
      where('user2', '==', user.uid),
      where('saved', '==', false),
      where('ended', '==', false)
    );

    const handleNewChat = (snapshot) => {
        snapshot.docChanges().forEach((change) => {
            if (change.type === 'added' && !hasNavigatedRef.current) {
                hasNavigatedRef.current = true;
                const delay = Math.max(0, minimumTimeRef.current - Date.now());
                setTimeout(() => {
                    const chatData = change.doc.data();
                    if (queueDocIdRef.current) {
                        deleteDoc(doc(db, 'anonymousChatQueueV2', queueDocIdRef.current)).catch(() => {});
                    }
                    navigate(`/chat/room/${change.doc.id}`, { state: { chatData, selectedMode: location.state?.selectedMode || 'global' } });
                }, delay);
            }
        });
    };

    const unsub1 = onSnapshot(q1, handleNewChat);
    const unsub2 = onSnapshot(q2, handleNewChat);

    const q = query(
      collection(db, 'anonymousChatQueueV2'),
      where('uid', '==', user.uid)
    );

    const unsub = onSnapshot(q, (snapshot) => {
      if (snapshot.empty) {
        setTimeout(() => {
            if (!hasNavigatedRef.current) navigate('/chat');
        }, 500);
        return;
      }
      const data = snapshot.docs[0].data();
      queueDocIdRef.current = snapshot.docs[0].id;
      setMode(data.mode);
    });

    return () => {
        unsub1(); unsub2(); unsub();
        if (queueDocIdRef.current && !hasNavigatedRef.current) {
            deleteDoc(doc(db, 'anonymousChatQueueV2', queueDocIdRef.current)).catch(() => {});
        }
    };
  }, [navigate]);

  const handleCancel = async () => {
    const user = auth.currentUser;
    if (user && queueDocIdRef.current) {
        try {
            hasNavigatedRef.current = true;
            await deleteDoc(doc(db, 'anonymousChatQueueV2', queueDocIdRef.current));
            navigate('/chat');
        } catch(e) {
            navigate('/chat');
        }
    } else {
        navigate('/chat');
    }
  };

  const switchToGlobal = async () => {
      if (isSwitching || !queueDocIdRef.current) return;
      setIsSwitching(true);
      try {
          await updateDoc(doc(db, 'anonymousChatQueueV2', queueDocIdRef.current), { mode: 'global' });
          setShowCampusTimeout(false);
      } catch (e) {
      } finally {
          setIsSwitching(false);
      }
  };

  if (identityMode === 'real') {
     return (
       <main className="w-full h-full border-r border-gray-800 flex flex-col items-center justify-center p-8 shrink-0 relative bg-[#0a0a0a]">
          <div className="w-16 h-16 rounded-full bg-[#0BD9E5]/20 text-[#0BD9E5] flex items-center justify-center mb-6">
             <MessageSquare size={32} />
          </div>
          <h2 className="text-[31px] font-extrabold mb-2 leading-tight text-center">Chat</h2>
          <p className="text-gray-500 text-[15px] text-center">Coming soon for Real Identity mode.</p>
       </main>
     );
  }

  return (
    <main className="w-full h-full border-r border-gray-800 flex flex-col shrink-0 relative overflow-hidden bg-black text-white font-poppins selection:bg-[#0BD9E5]/30">
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
            <div className="absolute top-[20%] left-[20%] w-[40%] h-[40%] bg-[#0BD9E5]/10 blur-[100px] rounded-full mix-blend-screen animate-pulse" style={{ animationDuration: '4s' }} />
            <div className="absolute bottom-[20%] right-[20%] w-[40%] h-[40%] bg-purple-500/10 blur-[100px] rounded-full mix-blend-screen animate-pulse" style={{ animationDuration: '5s' }} />
        </div>

        <div className="relative z-10 flex flex-col h-full items-center justify-center p-6 w-full max-w-md mx-auto">
            <div className="absolute top-4 left-4 right-4 flex justify-between items-center z-50">
                <button 
                    onClick={handleCancel}
                    className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10 transition-colors pointer-events-auto"
                >
                    <X size={20} className="text-white" />
                </button>
                <div className="w-10" />
            </div>

          <div className="relative w-72 h-72 flex items-center justify-center mb-8">
              <div className={`absolute inset-0 border-2 rounded-full bg-black/40 flex items-center justify-center ${mode === 'campus' ? 'border-[#0BD9E5]' : 'border-[#BD00FF]'}`}>
                 {[0, 1, 2].map((i) => (
                    <motion.div 
                        key={i}
                        initial={{ scale: 0.5, opacity: 0.8 }}
                        animate={{ scale: 1.5, opacity: 0 }}
                        transition={{ duration: 2, repeat: Infinity, delay: i * 0.6 }}
                        className={`absolute w-48 h-48 border rounded-full ${mode === 'campus' ? 'border-[#0BD9E5]' : 'border-[#BD00FF]'}`}
                    />
                 ))}
                 <motion.div 
                    animate={{ rotate: 360 }}
                    transition={{ repeat: Infinity, duration: 4, ease: "linear" }}
                    className={`absolute w-1/2 h-[2px] right-0 top-1/2 origin-left bg-gradient-to-r from-transparent ${mode === 'campus' ? 'to-[#0BD9E5]' : 'to-[#BD00FF]'}`}
                    style={{ transformOrigin: 'left' }}
                 />
                 <div className="absolute w-[80%] flex justify-between items-center z-10 px-4">
                     <div className="flex flex-col items-center gap-2">
                        <div className="w-16 h-16 rounded-full bg-[#222] border-2 border-white flex items-center justify-center overflow-hidden relative">
                           <img 
                               src={`/avatars/${randomNames[avatarIndex]}.png`}
                               alt="Looking" 
                               className="w-16 h-16 object-cover"
                               onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
                           />
                           <Ghost size={24} className="text-gray-500 absolute hidden" />
                        </div>
                     </div>
                     <motion.div animate={{ scale: [1, 1.2, 1] }} transition={{ repeat: Infinity, duration: 2 }}>
                         <ArrowLeftRight className={mode === 'campus' ? "text-[#0BD9E5]" : "text-[#BD00FF]"} size={24} />
                     </motion.div>
                     <div className="flex flex-col items-center gap-2">
                        <div className={`w-16 h-16 rounded-full bg-[#222] border-2 flex items-center justify-center overflow-hidden relative ${mode === 'campus' ? 'border-[#0BD9E5]' : 'border-[#BD00FF]'}`}>
                           <img 
                               src={`/avatars/${(randomNames[avatarIndex+1] || randomNames[0])}.png`}
                               alt="Found" 
                               className="w-16 h-16 object-cover"
                               onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
                           />
                           <Ghost size={24} className="text-gray-500 absolute hidden" />
                        </div>
                     </div>
                 </div>
              </div>
          </div>

          <div className="h-8">
             <h3 className="text-white font-bold text-lg text-center">{statusMessages[statusIndex]}</h3>
          </div>

          <AnimatePresence>
              {showCampusTimeout && mode === 'campus' && (
                  <motion.div 
                      initial={{ opacity: 0, y: 20 }} 
                      animate={{ opacity: 1, y: 0 }} 
                      exit={{ opacity: 0, y: 20 }}
                      className="mt-6 w-full flex flex-col items-center relative z-50 pointer-events-auto"
                  >
                      <button 
                          onClick={switchToGlobal}
                          disabled={isSwitching}
                          className="w-full relative overflow-hidden group bg-[#111] border border-[#BD00FF] rounded-2xl p-4 flex items-center justify-between hover:bg-white/5 transition-all"
                      >
                          <div className="absolute inset-0 bg-gradient-to-r from-[#BD00FF]/10 to-transparent group-hover:opacity-100 opacity-50 transition-opacity" />
                          <div className="flex flex-col items-start relative z-10 text-left">
                              <span className="text-white font-bold text-lg">Switch to Global</span>
                              <span className="text-gray-400 text-xs mt-1 max-w-[200px]">Taking too long? Expand your search to the entire multiverse!</span>
                          </div>
                          <Globe size={28} className="text-[#BD00FF] relative z-10" />
                      </button>
                  </motion.div>
              )}
          </AnimatePresence>

          {!showCampusTimeout && timerSeconds >= 5 && (
             <motion.div 
                initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className={`font-medium mt-4 text-sm flex items-center gap-2 ${mode === 'campus' ? 'text-[#0BD9E5]' : 'text-[#BD00FF]'}`}
             >
                <Zap size={14} /> 
                {timerSeconds}s elapsed...
             </motion.div>
          )}
        </div>
    </main>
  );
};

export default FindingMatchPage;
